'use strict';

define('vb/private/vx/extensionRegistry',[
  'vb/private/vx/extensionAdapterFactory',
  'vb/private/constants',
  'vb/private/utils',
  'urijs/URI',
  'vb/private/log',
  'vb/private/services/extensionServices',
], (ExtensionAdapterFactory, Constants, Utils, URI, Log, ExtensionServices) => {
  const log = Log.getLogger('/vb/private/vx/extensionRegistry');

  /**
   * The regex used to find and extract the app-packages id in an extension
   * App-package are always located in a self/app-packages folder and the descriptor
   * is app-pack.json.
   * @type {RegExp}
   */
  const appPackageRegex = /^self\/app-packages\/(.*)\/app-pack\.json$/;

  /**
   * The regex to find openapi3 files; in V1 Extension Registry format, they lived in "self".
   * The () group is used to capture the service name from the path.
   * @type {RegExp}
   */
  const v1ServiceRegex = /^self\/services\/([\w_$-]*)\/openapi[\w.]*\.json$/;
  const v2ServiceRegex = /^services\/([\w_$-]*)\/openapi[\w.]*\.json$/;

  const v1CatalogRegex = /^self\/services\/catalog.json$/;
  // this V2 pattern may be (close but) wrong;.
  // see https://confluence.oraclecorp.com/confluence/display/ABCS/Source-to-Built+Artifact+Transformation
  const v2CatalogRegex = /^services\/catalog.json$/;


  /**
   * The name given to the extension bundle stored in the extension.
   * @type {String}
   */
  const VB_APP_BUNDLE_NAME = 'vb-app-bundle.js';

  /**
   * A class to retrieve the extensions for the current application from the extension manager
   * The extension manager URL is defined in the app-flow.json under the extension property.
   */
  class ExtensionRegistry {
    /**
     * This is called from application.js
     *
     * @param  {Application} application the application object
     */
    constructor(application) {
      this.application = application;
      /**
       * An object to store the app-package extension content. The keys are app-package ids.
       * It is initialized when the application loads using getAppPackages()
       * @type {Object}
       */
      this.appPackages = {};
    }

    initialize() {
      // A promise that resolves with an array of manifest
      // Load the application metadata first in order to get the application id.
      // Also add the extension mapping (vx/extensionId) to requirejs before resolving.
      this.loadManifestPromise = this.application.loadMetadata()
        .then((definition) => ExtensionAdapterFactory.get().then((adapter) => {
          this.extensionAdapter = adapter;
          const promises = [
            adapter.loadManifest(definition.id),
            this.application.runtimeEnvironment.getExtensionManifest(),
          ];
          // Load the manifest from the extension manager and from DT and
          // replace the extensions with the ones from the DT manifest.
          return Promise.all(promises).then((manifests) => {
            const manifest = manifests[0];
            const dtManifest = manifests[1];
            if (dtManifest) {
              const dtExtensions = dtManifest.extensions || [];
              dtExtensions.forEach((dtExtension) => {
                const index = ExtensionRegistry
                  .getExtensionIndexFromManifest(manifest, dtExtension.id);
                if (index !== undefined) {
                  manifest.extensions[index] = dtExtension;
                } else {
                  manifest.extensions.push(dtExtension);
                }
              });
            }
            return adapter.cacheExtensions(manifest);
          });
        }))
        .then((cacheResult) => cacheResult.manifest)
        .catch((err) => {
          // Swallow the error so that it doesn't break the application, but no extension will be loaded
          log.error('Error loading extension registry, running baseline application', err);
          return {};
        });
    }

    /**
     * Returns a promise that resolves with an array of extensions or an empty array
     * @return {Promise<Array>} a promise that resolve to an array of extensions
     */
    getExtensions() {
      this.loadExtensionsPromise = this.loadExtensionsPromise || this.loadManifestPromise.then((manifest) => {
        const extensions = manifest.extensions || [];
        const bundlePaths = [];
        const mappings = {};

        extensions.forEach((extension) => {
          const baseUrl = extension.ui && extension.ui.baseUrl;
          const extensionId = extension.id;

          if (!baseUrl || !extensionId) {
            log.error('Invalid manifest for extension:', extensionId, 'version:', extension.version);
          }

          const files = extension.files || [];
          const bundlePath = files.find((path) => path.indexOf(VB_APP_BUNDLE_NAME) > 0);

          // If a bundle is defined in the list of files, use it. Otherwise defines a requirejs mapping
          // that will be used to load individual files
          if (bundlePath) {
            const url = `${baseUrl}/${bundlePath}`;
            bundlePaths.push(requirejs.toUrl(url));
            // keep track of which ones are bundled, for loading JS
            // eslint-disable-next-line no-param-reassign
            extension.isOptimized = true;

            log.info('Applying optimized extension:', extensionId, 'version:', extension.version);
          } else if (files.length > 0) {
            // eslint-disable-next-line no-param-reassign
            extension.isOptimized = false;
            const mapping = this.extensionAdapter.buildMapping(extensionId, baseUrl);
            log.info('Applying extension:', extensionId, 'version:', extension.version);
            Object.assign(mappings, mapping);
          } else {
            log.warn('Extension:', extensionId, 'version:', extension.version, 'is empty.');
          }
        });

        // Create a requirejs mapping for each extension that do not have a bundle so that resources
        // can be loaded with requirejs using the shortcut "vx/extensionId"
        if (Object.entries(mappings).length > 0) {
          requirejs.config({
            map: {
              '*': mappings,
            },
          });
        }

        // Load the bundles and return the extensions
        return Utils.getResources(bundlePaths).then(() => extensions);
      });

      return this.loadExtensionsPromise;
    }

    /**
     * Check if extensions need to be downloaded from an extension manager. If the extensions have been previously
     * downloaded to the device, version check is performed to upgrade or download new extensions
     *
     * @param {String} registryUrl  The url for the Extension Registry Manager  (required)
     *
     * @returns {Promise<Outcome<Array<Extension>>>} A Promise with an outcome containing list of 'extensions'
     * that need to be downloaded
     */
    checkForExtensions(registryUrl) {
      return ExtensionAdapterFactory.get(registryUrl)
        .then((adapter) => adapter.loadManifest(this.application.definition.id)
          .then(() => adapter.getExtensionsToDownload()));
    }

    /**
     * Downloads extensions to the device from an extension manager. If the extensions are already there on the device,
     * version check is performed to upgrade or download new extensions
     *
     * @param {String}  registryUrl  The url for the Extension Registry Manager  (required)
     * @param {String} progressUpdateActionId ID of action in current chain to execute for providing
     *  progress update during extension downloads
     * @param {ActionChain} actionChain the action chain under which 'progressUpdateActionId' action is invoked
     * @param {Object} actionContext available context for running the action 'progressUpdateActionId'
     * @returns {Promise<Outcome<Array<Extension>>>} A Promise with an outcome containing list of 'extensions'
     * that were downloaded
     */
    downloadExtensions(registryUrl, progressUpdateActionId, actionChain, actionContext) {
      return ExtensionAdapterFactory.get(registryUrl).then((adapter) => adapter
        .loadManifest(this.application.definition.id)
        .then((manifest) => adapter.cacheExtensions(manifest, progressUpdateActionId, actionChain, actionContext))
        .then((cacheResult) => cacheResult.cachedExtension));
    }

    /**
     * Loads all the extension for a specific container given its path. It returns a promise
     * that resolves in an array of extensions object, either PageExtension or FlowExtension.
     * @param  {String} path the path of the object for which we are looking for extensions
     * @param  {Container} container the container for which the extension is being loaded
     * @return {Promise} a promise to an array of extension objects
     */
    loadContainerExtensions(path, container) {
      return this.getExtensions().then((extensions) => {
        const promises = [];
        const basePath = `${container.extensionId}/${path}`;
        const Clazz = container.extensionClass;
        const extPath = `${basePath}${container.name}${Clazz.resourceSuffix}`;

        // Traverse the array of extension from first to last. The extension manager is responsible
        // for properly ordering this array of extensions given the dependencies in the extension manager.
        extensions.forEach((extension) => {
          const files = extension.files || [];

          // If the manifest contains an extension for this artifact, creates an extension object for it
          if (files.indexOf(extPath) >= 0) {
            const ext = new (Clazz)(extension, basePath, container);
            const promise = ext.load().then(() => ext);
            promises.push(promise);
          }
        });

        // All files are then loaded in parallel
        return Promise.all(promises);
      });
    }


    /**
     * Retrieve an array with all the app-package id available in the extensions
     * @return {Array<String>} array of app-package id available
     */
    getAppPackages() {
      return this.getExtensions().then((extensions) => {
        const results = [];
        // Traverse the array of extension from first to last. The extension manager is responsible
        // for properly ordering this array of extensions given the dependencies in the extension manager.
        extensions.forEach((extension) => {
          const files = extension.files || [];

          // Look for the package json files
          files.forEach((file) => {
            const match = file.match(appPackageRegex);
            const id = match && match[1];
            if (id) {
              // WARNING: when multiple extension defined the same app-package the last one wins
              log.warn('App-package', id, 'content has be redefined by extension', extension.id);
              this.appPackages[id] = extension;
              results.push(id);
            }
          });
        });

        return results;
      });
    }

    /**
     * Given an app package id, return the extension that defines it
     * @param  {String} id the app package id
     * @return {Object}    the app package definition
     */
    getAppPackageExtension(id) {
      return this.appPackages[id];
    }

    /**
     * path prefix of the designed layout file. always starts with 'dynamicLayouts/' and
     * ends with '/layout'.
     *
     * This method calls the service to get the layout templates
     *
     * @param pathPrefix {string}
     * @param descriptors {Array<object>} an array of descriptors describing the files expected
     * @see loadLayoutMaps()
     * @returns {Promise<{ name: string, data: string, source: <function | *>, template: string, _extInfo: {object} }>}
     */
    loadLayoutExtensions(pathPrefix, descriptors = []) {
      return this.getExtensions().then((extensions) => {
        const basePath = `base/${pathPrefix}`;

        const toLoadMap = this.createLayoutMaps(extensions, basePath);

        const promises = this.loadLayoutMaps(toLoadMap, descriptors);

        return Promise.all(promises)
          .then((all) => {
            const map = {};
            all.forEach((loadedInfo) => {
              if (loadedInfo) {
                map[loadedInfo.name] = map[loadedInfo.name] || {};
                map[loadedInfo.name][loadedInfo.type] = loadedInfo.contents;

                // also keep a map of 'paths' for each object
                // eslint-disable-next-line no-underscore-dangle
                map[loadedInfo.name]._extInfo = map[loadedInfo.name]._extInfo || {
                  isAbsolute: loadedInfo.isAbsolute,
                  isOptimized: loadedInfo.isOptimized,
                  paths: {},
                };
                // eslint-disable-next-line no-underscore-dangle
                map[loadedInfo.name]._extInfo.paths[loadedInfo.type] = loadedInfo.loaderPath;
              }
            });

            // now convert the map to a list

            const list = [];
            Object.keys(map).forEach((name) => {
              list.push(Object.assign({ name }, map[name]));
            });
            return list;
          })
          .catch((err) => {
            // for now, catch the error, and return an empty array
            log.error('Problem loading layout extensions, returning no extensions', err);
            return [];
          });
      });
    }

    /**
     * Creates an ExtensionServices Object for the application extension, and creates a name/file map
     * from the contents of the extension.
     *
     * @param extensionId
     * @param options { namespace: {string}, relativePath: {string}, expressionContext: {string},
     *   protocolRegistry: {ProtocolRegistry}, serviceFileMap: {object} }
     *   This is the standard options used to construct a Services object.
     *
     * @returns {Promise<T>}
     */
    loadServicesModel(extensionId, options) {
      // populated with the services we can find on the extension manifest
      const extensionServiceMap = {};

      return this.getExtensions()
        .then((extensions) => {
          const ext = extensions.find((ex) => ex.id === extensionId);
          if (ext) {
            ext.files.forEach((file) => {
              let name;
              let path;

              const match = file.match(v2ServiceRegex) || file.match(v1ServiceRegex);
              // [0] is the whole match, [1] is the first (and only) group
              if (match) {
                path = match[0];
                name = match[1];
              }

              if (name && path) {
                // we need to check if the extension has an explicit serviceFileMap declaration to ensure we are
                // not replacing it
                const declaredPath = options && options.serviceFileMap && options.serviceFileMap[name];
                if (!declaredPath) {
                  extensionServiceMap[name] = path;
                } else if (declaredPath !== path) {
                  // it would be weird to declare a path for a service that doesn't match its name,
                  // if one that did match its name already existed.
                  log.warn('Extension', extensionId, 'contains service metadata ', path,
                    '. The declared file will be used instead: ', declaredPath);
                }
              }
            });
          } else {
            // this should never happen
            log.warn('Unable to find extension services for extension, continuing: ', ext);
          }
          return this.findCatalog(ext);
        })
        .then((catalogPath) => {
          const optionsClone = Object.assign({ extensionServiceMap }, options);

          if (catalogPath) {
            optionsClone.extensions = optionsClone.extensions || {};
            optionsClone.extensions.catalogPaths = {
              [extensionId]: catalogPath,
            };
          }

          return new ExtensionServices(optionsClone);
        });
    }


    /**
     * if there is a catalog.json in self/, returns the vx-mapped path to the file
     * ex:  vx/ext2/self/services/catalog.json
     * @returns {string|undefined}
     */
    findCatalog(extension) {
      let found;
      extension.files.some((file) => {
        const match = file.match(v2CatalogRegex) || file.match(v1CatalogRegex);
        // [0] is the whole match, [1] is the first (and only) group
        if (match) {
          found = `${Constants.EXTENSION_PATH}${extension.id}/${file}`;
        }
        return !!found;
      });
      return found;
    }

    /**
     * iterate all extension files, grouping layout files by path prefix, then with extension as key
     *
     * {
     *   'dynamicLayouts/base/foo': {
     *     'ExtA': {
     *       id: 'ExtA',
     *       name: 'ExtA/1.0',
     *       files: ['layout-x.json']
     *     }
     *   },
     *   'ExtB': {
     *     id: 'ExtB',
     *     name: 'ExtB/1.0.1',
     *     files: ['layout-x.json', 'layout-x.js']
     *   }
     * }
     * @param extensions
     * @param basePath
     * @returns { {[path]: { [extId]: { id, name, files: [] }}}}
     * @private
     */
    // eslint-disable-next-line class-methods-use-this
    createLayoutMaps(extensions, basePath) {
      const toLoadMap = {};
      extensions.forEach((extension) => {
        const files = extension.files || [];

        // need to know if the requirejs 'vx' mapping is absolute, because requirejs will
        // need the '.js' extension for sources when it is absolute
        const baseUrl = (extension.ui && extension.ui.baseUrl) || '';
        const isAbsolute = new URI(baseUrl).is('absolute');

        // create a map of paths (without filename) to a list of files
        // Do an exact match without filename, so /foo/bar/leads matches /foo/bar/leadslayout-x.json,
        // but does NOT match /foo/bar/leads/child/address/layout-x.json
        // This basePath can come from multiple places, including legacy declaration; so add slash just in case.
        const baseURI = new URI(basePath.endsWith('/') ? basePath : `${basePath}/`);
        const matchingFiles = files.filter((file) => baseURI.equals(new URI(file).filename('')));

        matchingFiles.forEach((file) => {
          const parts = file.split(Constants.PATH_SEPARATOR);
          const filename = parts.pop();
          const key = parts.join(Constants.PATH_SEPARATOR);

          toLoadMap[key] = toLoadMap[key] || {};
          toLoadMap[key][extension.id] = toLoadMap[key][extension.id] || {
            id: extension.id,
            name: extension.id,
            baseUrl,
            isAbsolute,
            isOptimized: extension.isOptimized,
            files: [],
          };
          toLoadMap[key][extension.id].files.push(filename);
        });
      });
      return toLoadMap;
    }

    /**
     * Given a manifest, retrieve the index of the extension matching the id
     * @param  {Object} manifest
     * @param  {String} id
     * @return {Number} the index or undefined if no matching extension is found
     */
    static getExtensionIndexFromManifest(manifest, id) {
      const extensions = manifest.extensions || [];
      let result;

      for (let index = 0; index < extensions.length; index += 1) {
        const extension = extensions[index];
        if (extension.id === id) {
          result = index;
          break;
        }
      }

      return result;
    }


    /**
     * given a map created from createLayoutMaps, get an array of promises for all the layout resources.
     * The promise resolves with an array:
     * [{
     *     name,      // extension id
     *     contents,  // file contents
     *     pathKey,   // the path prefix whose layouts we were asked for
     *     type,      // data/source/template - the contract with the component provider
     *     ext,       // the file extension
     *   }]
     * @param toLoadMap
     * @param descriptors a structure that describes each expected file, and what to return
     * @param descriptors.property name of property in the returned object for this resource
     * @param descriptors.extensionFile file name, with extension, for the extension file
     * @param descriptors.module optional, file name without extension; used when loading a Javascript file
     *
     * @returns {[Object]}
     * @private
     */
    // eslint-disable-next-line class-methods-use-this
    loadLayoutMaps(toLoadMap, descriptors) {
      // now, iterate the list of files for each path 'key', and create a load Proimse
      const promises = [];
      Object.keys(toLoadMap).forEach((pathKey) => {
        const extInfo = toLoadMap[pathKey];
        // log.greenInfo('Loading layout extensions for:', pathKey);

        Object.keys(extInfo).forEach((extKey) => {
          const loadInfo = toLoadMap[pathKey][extKey];

          loadInfo.files.forEach((fileWithExt) => {
            // find the matching descriptor
            const descriptor = descriptors
              .find((desc) => desc.extensionFile && desc.extensionFile.endsWith(fileWithExt));
            // if the file in the manifest is not in the descriptor, we won't include it;
            // it is expected that the desired extension files are all represented by the descriptor.
            let fileName = fileWithExt;
            if (descriptor) {
              // depending on how we load, we might need to remove the extension, because of requireJS.
              // bufp-36947 when loading from a bundle, REMOVE the ".js" extension.
              // Otherwise, if loading an individual JS file, and we have mapped it to an absolute URL (which we do),
              // and we are not bundled, LEAVE the '.js' extension.
              if (descriptor.removeExtRelative
                && (loadInfo.isOptimized || (!loadInfo.isAbsolute && !loadInfo.isOptimized))) {
                fileName = Utils.removeFileExtension(fileName);
              }

              const loaderPath = `${Constants.EXTENSION_PATH}${loadInfo.id}/${pathKey}/${fileName}`;
              const promise = descriptor
                .extnLoader(loaderPath)
                .then((contents) => ({
                  name: loadInfo.name,
                  contents,
                  pathKey, // the path up to the filename
                  isOptimized: loadInfo.isOptimized,
                  isAbsolute: loadInfo.isAbsolute,
                  loaderPath,
                  type: descriptor.property, // name for the property passed to the components
                }))
                .catch((e) => {
                  // don't stop on the first failure, return what we can
                  log.error('Problem loading layout extensions, skipping: ', fileWithExt, 'error: ', e);
                  return null;
                });
              // resolve each promise with the contents, and the full name
              promises.push(promise);
            }
          });
        });
      });

      return promises;
    }
  }

  return ExtensionRegistry;
});

